import React, { useState, useEffect, useMemo } from "react";
import { Input } from "../../../shared/components/Input.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { createDefaultItem, GST_RATES, ITEM_UNITS } from "../models/item.js";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";
import { useInvoices } from "../../invoices/hooks/useInvoices.js";
import { usePurchases } from "../../purchases/hooks/usePurchases.js";
import { UNASSIGNED_COMPANY, getRecordCompanyKey } from "../../../shared/utils/companyScope.js";

export function ItemForm({ initialData = null, onSubmit, onCancel, loading = false }) {
  const [formData, setFormData] = useState(createDefaultItem());
  const [error, setError] = useState("");
  const { companies, activeCompanyId, defaultCompany } = useActiveCompany();
  const { invoices } = useInvoices();
  const { purchases } = usePurchases();
  const [companyIds, setCompanyIds] = useState([]);

  // Bills this item already has per company (a company with bills can't be unticked)
  const billsByCompany = useMemo(() => {
    const counts = {};
    if (!initialData?.id) return counts;
    const itemId = initialData.id;
    const name = String(initialData.itemName || initialData.name || "").trim().toLowerCase();

    const checkBill = (b) => {
      const items = Array.isArray(b.items) ? b.items : [];
      const hasItem = items.some((li) => {
        if (li.itemId && li.itemId === itemId) return true;
        if (li.id && li.id === itemId) return true;
        const liName = String(li.itemName || li.name || "").trim().toLowerCase();
        return name && liName === name;
      });
      if (!hasItem) return;

      const key = getRecordCompanyKey(b, companies);
      const id = key === UNASSIGNED_COMPANY ? defaultCompany?.id : key;
      if (id) counts[id] = (counts[id] || 0) + 1;
    };

    (invoices || []).forEach(checkBill);
    (purchases || []).forEach(checkBill);
    return counts;
  }, [initialData, invoices, purchases, companies, defaultCompany]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        itemName: initialData.itemName || initialData.name || "",
        itemType: initialData.itemType || "goods",
        gstCode: initialData.gstCode || initialData.sku || "",
        gstPercent: initialData.gstPercent !== undefined ? initialData.gstPercent : 18,
        basicPrice: initialData.basicPrice !== undefined ? initialData.basicPrice : (initialData.sellingPrice || ""),
        costPrice: initialData.costPrice || "",
        stock: initialData.stock !== undefined ? initialData.stock : "",
        reorderLevel: initialData.reorderLevel !== undefined ? initialData.reorderLevel : 10,
        unit: initialData.unit || "pcs",
        status: initialData.status || "active"
      });
      setCompanyIds(initialData.companyIds?.length ? initialData.companyIds : companies.map((c) => c.id));
    } else {
      setFormData(createDefaultItem());
      setCompanyIds(activeCompanyId ? [activeCompanyId] : (companies[0]?.id ? [companies[0].id] : []));
    }
  }, [initialData, activeCompanyId, companies]);

  const toggleCompany = (id) => {
    setError("");
    if (companyIds.includes(id)) {
      if (billsByCompany[id]) {
        const name = companies.find((c) => c.id === id)?.name || "this company";
        setError(`Has ${billsByCompany[id]} ${name} bill${billsByCompany[id] === 1 ? "" : "s"} — can't remove ${name}.`);
        return;
      }
      setCompanyIds(companyIds.filter((x) => x !== id));
    } else {
      setCompanyIds([...companyIds, id]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.itemName.trim()) {
      setError("Please provide an item / product name.");
      return;
    }
    if (formData.basicPrice === "" || Number(formData.basicPrice) < 0) {
      setError("Please provide a valid selling / basic price.");
      return;
    }
    if (!companyIds.length) {
      setError("Pick at least one company this item belongs to.");
      return;
    }

    setError("");
    try {
      await onSubmit({
        ...formData,
        companyIds: companies.map((c) => c.id).filter((id) => companyIds.includes(id)),
        basicPrice: Number(formData.basicPrice || 0),
        sellingPrice: Number(formData.basicPrice || 0),
        costPrice: Number(formData.costPrice || 0),
        gstPercent: Number(formData.gstPercent || 0),
        stock: Number(formData.stock || 0),
        reorderLevel: Number(formData.reorderLevel || 10)
      });
    } catch (err) {
      setError(err.message || "Failed to save item.");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      <Input
        label="Item / Product Name"
        id="item-name"
        name="itemName"
        value={formData.itemName}
        onChange={handleChange}
        placeholder="e.g. Cotton Grey Fabric 40s"
        required
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <div className="form-group">
          <label htmlFor="item-type" className="form-label">Item Type</label>
          <select
            id="item-type"
            name="itemType"
            value={formData.itemType}
            onChange={handleChange}
            className="form-control"
          >
            <option value="goods">Goods / Physical Stock</option>
            <option value="service">Service / Jobwork</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="item-unit" className="form-label">Unit of Measure</label>
          <select
            id="item-unit"
            name="unit"
            value={formData.unit}
            onChange={handleChange}
            className="form-control"
          >
            {ITEM_UNITS.map((u) => (
              <option key={u.id} value={u.id}>{u.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Input
          label="HSN / SAC Code"
          id="item-gstcode"
          name="gstCode"
          value={formData.gstCode}
          onChange={handleChange}
          placeholder="e.g. 5208"
        />

        <div className="form-group">
          <label htmlFor="item-gst" className="form-label">GST Tax Rate</label>
          <select
            id="item-gst"
            name="gstPercent"
            value={formData.gstPercent}
            onChange={handleChange}
            className="form-control"
          >
            {GST_RATES.map((rate) => (
              <option key={rate} value={rate}>{rate}% GST</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Input
          label="Selling Price (₹)"
          id="item-basicprice"
          name="basicPrice"
          type="number"
          step="0.01"
          value={formData.basicPrice}
          onChange={handleChange}
          placeholder="85.00"
          required
        />

        <Input
          label="Cost Price (₹)"
          id="item-cost"
          name="costPrice"
          type="number"
          step="0.01"
          value={formData.costPrice}
          onChange={handleChange}
          placeholder="60.00"
        />
      </div>

      {formData.itemType !== "service" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <Input
            label="Current Stock Quantity"
            id="item-stock"
            name="stock"
            type="number"
            value={formData.stock}
            onChange={handleChange}
            placeholder="500"
          />

          <Input
            label="Low-Stock Alert Level"
            id="item-reorder"
            name="reorderLevel"
            type="number"
            value={formData.reorderLevel}
            onChange={handleChange}
            placeholder="50"
          />
        </div>
      )}

      {/* Companies this item belongs to */}
      {companies.length > 1 && (
        <div className="form-group" data-testid="item-companies" style={{ marginTop: "0.75rem" }}>
          <label className="form-label">Companies</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
            {companies.map((c) => {
              const ticked = companyIds.includes(c.id);
              const bills = billsByCompany[c.id] || 0;
              const locked = ticked && bills > 0; // a company with bills for this item stays
              return (
                <label
                  key={c.id}
                  title={locked ? `Has ${bills} bill${bills === 1 ? "" : "s"} in ${c.name}, so it can't be removed` : undefined}
                  style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.88rem", cursor: locked ? "not-allowed" : "pointer" }}
                >
                  <input type="checkbox" checked={ticked} disabled={locked} onChange={() => toggleCompany(c.id)} />
                  <span style={{ flex: 1, minWidth: 0 }}>{c.name}</span>
                  {bills > 0 && (
                    <span style={{ fontSize: "0.72rem", color: locked ? "var(--warning, #b45309)" : "var(--text-dim)", whiteSpace: "nowrap" }}>
                      {locked ? "🔒 " : ""}{bills} bill{bills === 1 ? "" : "s"}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "6px", lineHeight: 1.5 }}>
            The item is listed, and can be billed, only in the ticked companies.
            {Object.values(billsByCompany).some(Boolean) && (
              <><br />🔒 = this item has bills in that company, so it stays. Delete those bills first to remove the company.</>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem" }}>
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            block
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          block
          loading={loading}
        >
          {initialData ? "Update Item" : "Save Item"}
        </Button>
      </div>
    </form>
  );
}

export default ItemForm;
